import { WritableSignal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as marked from 'marked';

import { LoggerService } from 'app/services/logger.service';
import { ChatMessage, SpotlightAgentChatService } from 'app/services/spotlight-agent-chat.service';
import { ChartData } from './ai-chart/ai-chart.component';
import {
  normalizeDocumentsData,
  normalizeSmartSummaryData,
  RICH_TOOL_NAMES,
  ToolContent,
  toolNameToContentType,
} from './tool-content.model';

interface AssistantMessage {
  readonly role: 'user' | 'assistant' | 'tool';
  readonly content?: SafeHtml;
  readonly text: string;
  readonly mode?: string;
  readonly chartData?: ChartData;
  readonly chartStatus?: 'loading' | 'error';
  readonly chartToolCallId?: string;
  readonly chartToolName?: string;
  readonly toolContent?: ToolContent;
  readonly toolContentStatus?: 'loading' | 'error';
  readonly toolContentToolCallId?: string;
  readonly toolContentToolName?: string;
}

interface ToolResult {
  readonly toolName: string;
  readonly toolCallId?: string;
  readonly output?: unknown;
}

interface HandleStreamingFallbackParams {
  readonly sessionId: string;
  readonly messages: ChatMessage[];
  readonly dataSource: { projectId: string; collectionId: string };
  readonly spotlightAgentChatService: SpotlightAgentChatService;
  readonly addMessage: (message: AssistantMessage) => Promise<void>;
  readonly addToolContentMessage: (toolName: string, output: unknown) => Promise<void>;
  readonly addChartMessage: (chartData: ChartData) => Promise<void>;
  readonly normalizeToolOutput: (toolName: string, output: unknown) => unknown;
  readonly normalizeChartOutput: (output: unknown) => ChartData | null;
  readonly errorText: string;
}

interface FetchChartOutputsParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly sessionId: string;
  readonly spotlightAgentChatService: SpotlightAgentChatService;
  readonly normalizeChartOutput: (output: unknown) => ChartData | null;
  readonly resolveChartPlaceholder: (
    toolCallId: string | undefined,
    toolName: string,
    chartOutput: ChartData | null
  ) => boolean;
  readonly addChartMessage: (chartData: ChartData) => Promise<void>;
  readonly markUnresolvedChartPlaceholdersAsError: () => void;
}

interface FetchToolOutputsParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly sessionId: string;
  readonly spotlightAgentChatService: SpotlightAgentChatService;
  readonly normalizeToolOutput: (toolName: string, output: unknown) => unknown;
  readonly resolveToolPlaceholder: (
    toolCallId: string | undefined,
    toolName: string,
    output: unknown
  ) => boolean;
  readonly addToolContentMessage: (toolName: string, output: unknown) => Promise<void>;
  readonly markUnresolvedChartPlaceholdersAsError: () => void;
  readonly markUnresolvedToolPlaceholdersAsError: () => void;
}

interface InsertChartPlaceholderParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly streamingMessageIndex: number | null;
  readonly toolCallId: string | undefined;
  readonly toolName: string;
  readonly emptyHtml: SafeHtml;
  readonly syncHistory: () => void;
}

interface ResolveChartPlaceholderParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly toolCallId: string | undefined;
  readonly toolName: string;
  readonly chartOutput: ChartData | null;
  readonly syncHistory: () => void;
}

interface MarkUnresolvedChartPlaceholdersParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly syncHistory: () => void;
}

interface InsertToolPlaceholderParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly streamingMessageIndex: number | null;
  readonly toolCallId: string | undefined;
  readonly toolName: string;
  readonly emptyHtml: SafeHtml;
  readonly syncHistory: () => void;
}

interface ResolveToolPlaceholderParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly toolCallId: string | undefined;
  readonly toolName: string;
  readonly output: unknown;
  readonly syncHistory: () => void;
}

interface MarkUnresolvedToolPlaceholdersParams {
  readonly messages: WritableSignal<AssistantMessage[]>;
  readonly syncHistory: () => void;
}

const MAX_CHART_OUTPUT_DEPTH = 5;
const SUPPORTED_CHART_TYPES = new Set<string>(['bar', 'line', 'pie']);

/**
 * Helper for ask-mode streaming and chart lifecycle operations.
 */
export class SpotlightAssistantAskModeHelper {
  constructor(private readonly logger: LoggerService) {}

  stripMarkdownImages(text: string): string {
    let result = text.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
    result = result.replace(/!\[[^\]]*\]/g, '');
    result = result.replace(/<img[^>]*>/gi, '');
    result = result.replace(/data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g, '');
    result = result.replace(/\n{3,}/g, '\n\n');
    return result.trim();
  }

  private stripMarkdownTables(text: string): string {
    return text.replace(/(?:^|\n)\|[^\n]+\|\s*\n\|[\s:|-]+\|\s*\n(?:\|[^\n]+\|\s*\n?)*/g, '\n');
  }

  updateStreamingMessage(
    messages: WritableSignal<AssistantMessage[]>,
    sanitizer: DomSanitizer,
    index: number,
    text: string
  ): void {
    const currentMessages = messages();
    if (index >= currentMessages.length) {
      return;
    }
    const previousMessage = index > 0 ? currentMessages[index - 1] : undefined;
    const sanitizedText =
      previousMessage?.toolContentToolName === 'displayTable'
        ? this.stripMarkdownTables(text).trim()
        : text;
    const updatedMessages = [...currentMessages];
    updatedMessages[index] = {
      ...updatedMessages[index],
      text: sanitizedText,
      content: sanitizer.bypassSecurityTrustHtml(marked.parse(sanitizedText) as string),
    };
    messages.set(updatedMessages);
  }

  async addChartMessage(
    chartData: ChartData,
    processMessage: (message: AssistantMessage) => Promise<AssistantMessage>,
    messages: WritableSignal<AssistantMessage[]>
  ): Promise<void> {
    const chartMessage: AssistantMessage = {
      role: 'assistant',
      text: chartData.config.title || 'Chart',
      mode: 'ask',
      chartData,
    };
    const processed = await processMessage(chartMessage);
    messages.update((existingMessages) => [...existingMessages, { ...processed, chartData }]);
  }

  async addToolContentMessage(
    toolName: string,
    output: unknown,
    processMessage: (message: AssistantMessage) => Promise<AssistantMessage>,
    messages: WritableSignal<AssistantMessage[]>
  ): Promise<void> {
    if (toolName === 'displayChart') {
      const chartData = this.normalizeChartOutput(output);
      if (chartData) {
        await this.addChartMessage(chartData, processMessage, messages);
      }
      return;
    }
    const contentType = toolNameToContentType(toolName);
    if (!contentType || !output) {
      return;
    }
    const toolMessage: AssistantMessage = {
      role: 'assistant',
      text: '',
      mode: 'ask',
      toolContent: { type: contentType, data: output } as ToolContent,
      toolContentToolName: toolName,
    };
    const processed = await processMessage(toolMessage);
    messages.update((existingMessages) => [
      ...existingMessages,
      { ...processed, toolContent: toolMessage.toolContent },
    ]);
  }

  async handleStreamingFallback(params: HandleStreamingFallbackParams): Promise<void> {
    try {
      const response = await params.spotlightAgentChatService.sendMessage(
        params.sessionId,
        params.messages,
        params.dataSource.projectId,
        params.dataSource.collectionId,
        true
      );
      if (typeof response.text === 'string' && response.text.trim().length > 0) {
        await params.addMessage({ role: 'assistant', text: response.text, mode: 'ask' });
      }
      if (response.toolCalls && response.toolCalls.length > 0) {
        for (const toolCall of response.toolCalls) {
          if (!RICH_TOOL_NAMES.has(toolCall.toolName)) {
            continue;
          }
          await params.addMessage({ role: 'tool', text: `${toolCall.toolName}`, mode: 'ask' });
          if (!toolCall.output) {
            continue;
          }
          const normalizedOutput = params.normalizeToolOutput(toolCall.toolName, toolCall.output);
          if (!normalizedOutput) {
            this.logger.warn(
              `[SpotlightAssistant] fallback ${toolCall.toolName} output missing or invalid`,
              { output: toolCall.output }
            );
            continue;
          }
          if (toolCall.toolName === 'displayChart') {
            const chartOutput = params.normalizeChartOutput(normalizedOutput);
            if (chartOutput) {
              await params.addChartMessage(chartOutput);
              continue;
            }
            this.logger.warn('[SpotlightAssistant] fallback chart output missing or invalid', {
              output: toolCall.output,
            });
            continue;
          }
          await params.addToolContentMessage(toolCall.toolName, normalizedOutput);
        }
      }
    } catch (error) {
      this.logger.error('Fallback chat error:', { error });
      const errorMessage = error instanceof Error ? error.message : params.errorText;
      await params.addMessage({
        role: 'assistant',
        text: errorMessage || params.errorText,
        mode: 'ask',
      });
    }
  }

  async fetchChartOutputs(params: FetchChartOutputsParams): Promise<void> {
    try {
      const toolResults = (await params.spotlightAgentChatService.getToolResults(
        params.sessionId
      )) as ToolResult[];
      const chartResults = toolResults.filter(
        (result) => result.toolName === 'displayChart' && result.output
      );
      const handledToolCallIds = new Set<string>();
      for (const result of chartResults) {
        if (result.toolCallId) {
          if (handledToolCallIds.has(result.toolCallId)) {
            continue;
          }
          handledToolCallIds.add(result.toolCallId);
          const alreadyRendered = params
            .messages()
            .some(
              (message) => message.chartToolCallId === result.toolCallId && !!message.chartData
            );
          if (alreadyRendered) {
            continue;
          }
        }
        const hasMatchingPlaceholder = params
          .messages()
          .some(
            (message) =>
              message.chartStatus === 'loading' &&
              message.chartToolName === 'displayChart' &&
              (result.toolCallId == null || message.chartToolCallId === result.toolCallId)
          );
        if (!hasMatchingPlaceholder) {
          continue;
        }
        const chartOutput = params.normalizeChartOutput(result.output);
        if (chartOutput) {
          const resolved = params.resolveChartPlaceholder(
            result.toolCallId,
            'displayChart',
            chartOutput
          );
          if (!resolved) {
            continue;
          }
        } else {
          this.logger.warn('[SpotlightAssistant] persisted chart output invalid', {
            output: result.output,
          });
        }
      }
      params.markUnresolvedChartPlaceholdersAsError();
    } catch (error) {
      this.logger.warn('[SpotlightAssistant] Failed to fetch chart outputs', { error });
      params.markUnresolvedChartPlaceholdersAsError();
    }
  }

  async fetchToolOutputs(params: FetchToolOutputsParams): Promise<void> {
    try {
      const toolResults = await params.spotlightAgentChatService.getToolResults(params.sessionId);
      const handledToolCallIds = new Set<string>();
      for (const result of toolResults) {
        if (!RICH_TOOL_NAMES.has(result.toolName) || !result.output) {
          continue;
        }
        const isChart = result.toolName === 'displayChart';
        if (result.toolCallId) {
          if (handledToolCallIds.has(result.toolCallId)) {
            continue;
          }
          handledToolCallIds.add(result.toolCallId);
          const alreadyRendered = params
            .messages()
            .some((message) =>
              isChart
                ? message.chartToolCallId === result.toolCallId && !!message.chartData
                : message.toolContentToolCallId === result.toolCallId && !!message.toolContent
            );
          if (alreadyRendered) {
            continue;
          }
        }
        const hasMatchingPlaceholder = params
          .messages()
          .some((message) =>
            isChart
              ? message.chartStatus === 'loading' &&
                message.chartToolName === result.toolName &&
                (result.toolCallId == null || message.chartToolCallId === result.toolCallId)
              : message.toolContentStatus === 'loading' &&
                message.toolContentToolName === result.toolName &&
                (result.toolCallId == null || message.toolContentToolCallId === result.toolCallId)
          );
        if (!hasMatchingPlaceholder) {
          continue;
        }
        const normalized = params.normalizeToolOutput(result.toolName, result.output);
        params.resolveToolPlaceholder(result.toolCallId, result.toolName, normalized ?? null);
      }
      params.markUnresolvedChartPlaceholdersAsError();
      params.markUnresolvedToolPlaceholdersAsError();
    } catch (error) {
      this.logger.warn('[SpotlightAssistant] Failed to fetch tool outputs', { error });
      params.markUnresolvedChartPlaceholdersAsError();
      params.markUnresolvedToolPlaceholdersAsError();
    }
  }

  insertChartPlaceholderAfter(params: InsertChartPlaceholderParams): number {
    const placeholder: AssistantMessage = {
      role: 'assistant',
      text: '',
      mode: 'ask',
      chartStatus: 'loading',
      chartToolCallId: params.toolCallId,
      chartToolName: params.toolName,
    };
    const newStreamingMessage: AssistantMessage = {
      role: 'assistant',
      text: '',
      content: params.emptyHtml,
      mode: 'ask',
    };
    let newStreamingIndex = 0;
    params.messages.update((existingMessages) => {
      const next = [...existingMessages];
      const insertIndex =
        params.streamingMessageIndex !== null
          ? Math.max(0, Math.min(params.streamingMessageIndex + 1, next.length))
          : next.length;
      next.splice(insertIndex, 0, placeholder, newStreamingMessage);
      newStreamingIndex = insertIndex + 1;
      return next;
    });
    params.syncHistory();
    return newStreamingIndex;
  }

  resolveChartPlaceholder(params: ResolveChartPlaceholderParams): boolean {
    let resolved = false;
    params.messages.update((existingMessages) => {
      const next = [...existingMessages];
      const index = this.findChartPlaceholderIndex(next, params.toolCallId, params.toolName);
      if (index < 0) {
        return existingMessages;
      }
      const existing = next[index];
      resolved = true;
      next[index] = {
        ...existing,
        chartData: params.chartOutput ?? undefined,
        chartStatus: params.chartOutput ? undefined : 'error',
        chartToolCallId: params.toolCallId ?? existing.chartToolCallId,
        chartToolName: params.toolName,
      };
      return next;
    });
    if (resolved) {
      params.syncHistory();
    }
    return resolved;
  }

  markUnresolvedChartPlaceholdersAsError(params: MarkUnresolvedChartPlaceholdersParams): void {
    let didUpdate = false;
    params.messages.update((existingMessages) => {
      const next = existingMessages.map((message) => {
        if (message.chartStatus !== 'loading' || message.chartToolName !== 'displayChart') {
          return message;
        }
        didUpdate = true;
        return { ...message, chartStatus: 'error' as const };
      });
      return didUpdate ? next : existingMessages;
    });
    if (didUpdate) {
      params.syncHistory();
    }
  }

  insertToolPlaceholderAfter(params: InsertToolPlaceholderParams): number {
    if (params.toolName === 'displayChart') {
      return this.insertChartPlaceholderAfter(params);
    }
    const placeholder: AssistantMessage = {
      role: 'assistant',
      text: '',
      mode: 'ask',
      toolContentStatus: 'loading',
      toolContentToolCallId: params.toolCallId,
      toolContentToolName: params.toolName,
    };
    const newStreamingMessage: AssistantMessage = {
      role: 'assistant',
      text: '',
      content: params.emptyHtml,
      mode: 'ask',
    };
    let newStreamingIndex = 0;
    params.messages.update((existingMessages) => {
      const next = [...existingMessages];
      const insertIndex =
        params.streamingMessageIndex !== null
          ? Math.max(0, Math.min(params.streamingMessageIndex + 1, next.length))
          : next.length;
      next.splice(insertIndex, 0, placeholder, newStreamingMessage);
      newStreamingIndex = insertIndex + 1;
      return next;
    });
    params.syncHistory();
    return newStreamingIndex;
  }

  resolveToolPlaceholder(params: ResolveToolPlaceholderParams): boolean {
    if (params.toolName === 'displayChart') {
      return this.resolveChartPlaceholder({
        messages: params.messages,
        toolCallId: params.toolCallId,
        toolName: params.toolName,
        chartOutput: params.output as ChartData | null,
        syncHistory: params.syncHistory,
      });
    }
    const contentType = toolNameToContentType(params.toolName);
    if (!contentType || !params.output) {
      if (params.toolName === 'displayDocuments') {
        return this.clearToolPlaceholder(
          params.messages,
          params.toolCallId,
          params.toolName,
          params.syncHistory
        );
      }
      return this.markToolPlaceholderAsError(
        params.messages,
        params.toolCallId,
        params.toolName,
        params.syncHistory
      );
    }
    let resolved = false;
    params.messages.update((existingMessages) => {
      const next = [...existingMessages];
      const index = this.findToolPlaceholderIndex(next, params.toolCallId, params.toolName);
      if (index < 0) {
        return existingMessages;
      }
      resolved = true;
      next[index] = {
        ...next[index],
        toolContent: { type: contentType, data: params.output } as ToolContent,
        toolContentStatus: undefined,
        toolContentToolCallId: params.toolCallId ?? next[index].toolContentToolCallId,
        toolContentToolName: params.toolName,
      };
      return next;
    });
    if (resolved) {
      params.syncHistory();
    }
    return resolved;
  }

  normalizeToolOutput(toolName: string, output: unknown): unknown {
    if (toolName === 'displayChart') {
      return this.normalizeChartOutput(output);
    }
    if (!output) {
      return null;
    }
    if (typeof output === 'string') {
      try {
        return this.normalizeToolOutput(toolName, JSON.parse(output));
      } catch {
        return null;
      }
    }
    if (typeof output === 'object') {
      const container = output as { output?: unknown; result?: unknown };
      const normalizedOutput = container.output ?? container.result ?? output;
      if (toolName === 'displayDocuments') {
        return normalizeDocumentsData(normalizedOutput);
      }
      if (toolName === 'displaySmartSummary') {
        return normalizeSmartSummaryData(normalizedOutput);
      }
      return normalizedOutput;
    }
    return null;
  }

  markUnresolvedToolPlaceholdersAsError(params: MarkUnresolvedToolPlaceholdersParams): void {
    let didUpdate = false;
    params.messages.update((existingMessages) => {
      const next = existingMessages.map((message) => {
        if (message.toolContentStatus === 'loading') {
          didUpdate = true;
          return { ...message, toolContentStatus: 'error' as const };
        }
        return message;
      });
      return didUpdate ? next : existingMessages;
    });
    if (didUpdate) {
      params.syncHistory();
    }
  }

  normalizeChartOutput(output: unknown): ChartData | null {
    if (!output) {
      return null;
    }
    if (typeof output === 'string') {
      try {
        const parsed = JSON.parse(output) as ChartData;
        return this.normalizeValidatedChartOutput(parsed?.config && parsed.data ? parsed : null);
      } catch (error) {
        this.logger.warn('[SpotlightAssistant] Failed to parse chart output string', { error });
        return null;
      }
    }
    if (typeof output === 'object') {
      return this.normalizeValidatedChartOutput(this.extractChartOutput(output));
    }
    return null;
  }

  private findChartPlaceholderIndex(
    messages: AssistantMessage[],
    toolCallId: string | undefined,
    toolName: string
  ): number {
    if (toolCallId) {
      const indexById = messages.findIndex(
        (message) => message.chartStatus === 'loading' && message.chartToolCallId === toolCallId
      );
      if (indexById >= 0) {
        return indexById;
      }
    }
    return messages.findIndex(
      (message) => message.chartStatus === 'loading' && message.chartToolName === toolName
    );
  }

  private findToolPlaceholderIndex(
    messages: AssistantMessage[],
    toolCallId: string | undefined,
    toolName: string
  ): number {
    if (toolCallId) {
      const indexById = messages.findIndex(
        (message) =>
          message.toolContentStatus === 'loading' && message.toolContentToolCallId === toolCallId
      );
      if (indexById >= 0) {
        return indexById;
      }
    }
    return messages.findIndex(
      (message) =>
        message.toolContentStatus === 'loading' && message.toolContentToolName === toolName
    );
  }

  private markToolPlaceholderAsError(
    messages: WritableSignal<AssistantMessage[]>,
    toolCallId: string | undefined,
    toolName: string,
    syncHistory: () => void
  ): boolean {
    let found = false;
    messages.update((existingMessages) => {
      const next = [...existingMessages];
      const index = this.findToolPlaceholderIndex(next, toolCallId, toolName);
      if (index < 0) {
        return existingMessages;
      }
      found = true;
      next[index] = { ...next[index], toolContentStatus: 'error' };
      return next;
    });
    if (found) {
      syncHistory();
    }
    return found;
  }

  private clearToolPlaceholder(
    messages: WritableSignal<AssistantMessage[]>,
    toolCallId: string | undefined,
    toolName: string,
    syncHistory: () => void
  ): boolean {
    let found = false;
    messages.update((existingMessages) => {
      const index = this.findToolPlaceholderIndex(existingMessages, toolCallId, toolName);
      if (index < 0) {
        return existingMessages;
      }
      found = true;
      return existingMessages.filter((_, messageIndex) => messageIndex !== index);
    });
    if (found) {
      syncHistory();
    }
    return found;
  }

  private isChartToolOutput(candidate: unknown): candidate is ChartData {
    if (!candidate || typeof candidate !== 'object') {
      return false;
    }
    const chartCandidate = candidate as ChartData;
    return !!chartCandidate.config && Array.isArray(chartCandidate.data);
  }

  private normalizeValidatedChartOutput(chartOutput: ChartData | null): ChartData | null {
    if (!chartOutput) {
      return null;
    }
    const chartOutputWithError = chartOutput as ChartData & { error?: unknown };
    if (
      typeof chartOutputWithError.error === 'string' &&
      chartOutputWithError.error.trim().length > 0
    ) {
      this.logger.warn('[SpotlightAssistant] chart output returned an execution error');
      return null;
    }
    const rawType = chartOutput.config?.type;
    const normalizedType =
      typeof rawType === 'string' && rawType.trim().length > 0 ? rawType : 'bar';
    if (!SUPPORTED_CHART_TYPES.has(normalizedType)) {
      this.logger.warn('[SpotlightAssistant] unsupported chart type', { type: normalizedType });
      return null;
    }
    return {
      ...chartOutput,
      config: {
        ...chartOutput.config,
        type: normalizedType,
      },
    };
  }

  private extractChartOutput(candidate: unknown, depth: number = 0): ChartData | null {
    if (depth > MAX_CHART_OUTPUT_DEPTH) {
      return null;
    }
    if (Array.isArray(candidate)) {
      for (const item of candidate) {
        const nested = this.extractChartOutput(item, depth + 1);
        if (nested) {
          return nested;
        }
      }
      return null;
    }
    if (this.isChartToolOutput(candidate)) {
      return candidate;
    }
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }
    const container = candidate as { output?: unknown; result?: unknown };
    if (typeof container.output === 'string') {
      const parsedOutput = this.parseChartOutputString(container.output);
      if (parsedOutput) {
        return parsedOutput;
      }
    }
    const outputCandidate = this.extractChartOutput(container.output, depth + 1);
    if (outputCandidate) {
      return outputCandidate;
    }
    if (typeof container.result === 'string') {
      const parsedResult = this.parseChartOutputString(container.result);
      if (parsedResult) {
        return parsedResult;
      }
    }
    return this.extractChartOutput(container.result, depth + 1);
  }

  private parseChartOutputString(payload: string): ChartData | null {
    const trimmed = payload.trim();
    const candidates: string[] = [trimmed];
    const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    if (fencedMatch?.[1]) {
      candidates.unshift(fencedMatch[1].trim());
    }
    const firstBrace = trimmed.indexOf('{');
    const lastBrace = trimmed.lastIndexOf('}');
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      candidates.unshift(trimmed.slice(firstBrace, lastBrace + 1));
    }
    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate) as unknown;
        const extracted = this.extractChartOutput(parsed);
        if (extracted) {
          return extracted;
        }
      } catch {
        continue;
      }
    }
    this.logger.warn('[SpotlightAssistant] Failed to parse chart output string');
    return null;
  }
}
